import { useParams } from '@tanstack/react-router';
import { useLoginPools, useLoginPool } from '_/hooks/useLoginPools';
import { ILoginPool } from '_/types/loginPools';
import React, { createContext, useContext, ReactNode } from 'react';

import { useOrganization } from './OrganizationContext';
import { useProject } from './ProjectContext';

interface LoginPoolContextValue {
  loginPools: ILoginPool[];
  selectedLoginPool: ILoginPool | null;
  selectedLoginPoolSlug: string | null;
  isLoading: boolean;
  refetch: () => void;
}

const LoginPoolContext = createContext<LoginPoolContextValue | undefined>(undefined);

export function LoginPoolProvider({ children }: { children: ReactNode }) {
  const params = useParams({ strict: false }) as Partial<{ loginPoolSlug?: string }>;
  const loginPoolSlug = params.loginPoolSlug || '';
  const { selectedProject, selectedProjectSlug } = useProject();
  const { selectedOrgSlug } = useOrganization();

  const {
    data: loginPools = [],
    isLoading: isLoadingList,
    refetch,
  } = useLoginPools(selectedProject?.id, selectedOrgSlug || undefined, selectedProjectSlug || undefined);

  // Fetch individual login pool for detail pages
  const { data: selectedLoginPool, isLoading: isLoadingSingle } = useLoginPool(
    selectedProject?.id,
    loginPoolSlug && loginPoolSlug !== '_new' ? loginPoolSlug : undefined,
  );

  const value: LoginPoolContextValue = {
    loginPools: loginPools,
    selectedLoginPool: selectedLoginPool || null,
    selectedLoginPoolSlug: loginPoolSlug || null,
    isLoading: isLoadingList || isLoadingSingle,
    refetch: refetch,
  };

  return <LoginPoolContext.Provider value={value}>{children}</LoginPoolContext.Provider>;
}

export function useLoginPoolContext(): LoginPoolContextValue {
  const context = useContext(LoginPoolContext);
  if (context === undefined) {
    throw new Error('useLoginPoolContext must be used within a LoginPoolProvider');
  }
  return context;
}
